import React, { useState, useEffect } from 'react';
import { Heart, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Detect mobile device
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
    || window.innerWidth < 768;
};

const LikeButton = ({ imageId, initialLikes = 0, size = 'default', showCount = true, showLabel = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(initialLikes);
  const [loading, setLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    if (user) {
      checkLikeStatus();
    }
  }, [user, imageId]);

  useEffect(() => {
    setLikes(initialLikes);
  }, [initialLikes]);

  const checkLikeStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/images/${imageId}/like-status`, {
        withCredentials: true
      });
      setLiked(response.data.liked);
    } catch (error) {
      // Silently fail - user might not be logged in
    }
  };

  const handleLike = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      // For mobile: redirect to login page and store pending like
      if (isMobileDevice()) {
        sessionStorage.setItem('pendingLikeImageId', imageId.toString());
        navigate('/login');
        return;
      }
      // For desktop: show popup
      setShowLoginModal(true);
      return;
    }

    if (loading) return;

    performLike();
  };

  const performLike = async () => {
    setLoading(true);
    
    // Optimistic update
    const newLiked = !liked;
    const newLikes = newLiked ? likes + 1 : Math.max(0, likes - 1);
    setLiked(newLiked);
    setLikes(newLikes);

    try {
      const response = await axios.post(`${API_BASE}/images/${imageId}/like`, {}, {
        withCredentials: true
      });
      // Update with server response
      setLiked(response.data.liked);
      setLikes(response.data.likes);
    } catch (error) {
      // Revert on error
      setLiked(!newLiked);
      setLikes(likes);
      console.error('Error toggling like:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Store pending like for after login
    sessionStorage.setItem('pendingLikeImageId', imageId.toString());
    setShowLoginModal(false);
    navigate('/login');
  };

  const handleCloseModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowLoginModal(false);
  };

  const handleBackdropClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowLoginModal(false);
  };

  const sizeClasses = {
    small: 'p-1.5',
    default: 'p-2',
    large: 'p-3'
  };

  const iconSizes = {
    small: 'w-4 h-4',
    default: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  return (
    <>
      <button
        onClick={handleLike}
        disabled={loading}
        className={`group flex items-center space-x-1 rounded-full transition-all duration-200 ${
          liked 
            ? 'text-red-500' 
            : 'text-gray-600 hover:text-red-500'
        } ${sizeClasses[size]} hover:bg-red-50 disabled:opacity-50`}
        data-testid={`like-button-${imageId}`}
        aria-label={liked ? 'Unlike' : 'Like'}
      >
        <Heart 
          className={`${iconSizes[size]} transition-transform duration-200 ${
            liked ? 'fill-current scale-110' : 'group-hover:scale-110'
          } ${loading ? 'animate-pulse' : ''}`}
        />
        {showCount && (
          <span className={`font-medium ${size === 'small' ? 'text-xs' : 'text-sm'}`}>
            {showLabel
              ? `${likes} ${likes === 1 ? 'Like' : 'Likes'}`
              : (likes > 0 ? likes : '')}
          </span>
        )}
      </button>

      {/* Login Modal - Desktop Only */}
      {showLoginModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleBackdropClick}
        >
          <div 
            className="bg-white rounded-xl max-w-sm w-full p-6 text-center relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="login-modal"
          >
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
            
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <Heart className="w-8 h-8 text-red-500" />
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Like this photo?
            </h3>
            <p className="text-gray-600 mb-6">
              Sign in to save your favorite photos and build your collection.
            </p>
            
            <div className="space-y-3">
              <Button 
                onClick={handleLogin}
                className="w-full"
                data-testid="login-modal-signin"
              >
                Sign in to Like
              </Button>
              <Button 
                variant="outline"
                onClick={handleCloseModal}
                className="w-full"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LikeButton;
